import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { boardDay, boardWeek, type BoardDay, type BoardWeek } from '@/lib/dispatchBoard';
import { indexTechnicianDays, type TechnicianBoardData } from '@/lib/availability';
import { parseIsoDate, toIsoDate, type CivilDate } from '@/lib/timezone';

export type Job = components['schemas']['JobResponse'];
export type UserSummary = components['schemas']['UserSummaryResponse'];

// The account query moved to `@/api/useAccount` at M1-04 — the customer's
// service history needs the same timezone this board does. Re-exported here
// so the board's own imports stay in one place.
export { useAccount, accountQueryKey, type Account } from '@/api/useAccount';

/**
 * The technicians the board draws a column for.
 *
 * Filtered to Active Technicians because those are exactly the people the API
 * will accept an assignment for — `POST /jobs/{id}/assign` 404s anything else
 * (M1-08). A column a dispatcher cannot drop work into is worse than no
 * column, so the two lists are kept deliberately identical.
 */
/**
 * The active technicians in the account.
 *
 * `enabled` exists because `GET /users` needs `users.read`, which a Technician
 * does not hold (M1-22): every caller that renders for them — the job list's
 * filters, the job detail page's assignment box — has to be able to NOT ask,
 * or every page load from a phone starts with a 403 nobody reads.
 */
export function useTechnicians(enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['users', 'technicians'],
    queryFn: async () => {
      // Same `!response.ok || !data` shape as the other two, and for the same
      // reason. (`GET /api/v1/users` declares only a 200 anyway, so
      // openapi-fetch types its `error` as `never` and a guard on it would
      // narrow `data` away to `never[]` — the shape has to be this one here.)
      const { data, response } = await apiClient.GET('/api/v1/users');
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/users failed: ${response.status}`);
      }
      return data
        .filter((user) => user.role === 'Technician' && user.status === 'Active')
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * The window of time a board is showing, and a stable cache key for it.
 *
 * A window rather than a day since M1-10: the week view fetches its seven days
 * in ONE request instead of seven, and splits them up client-side with
 * `occursOnDay`. Seven requests would each pay the round trip, and the toggle
 * between views would then be seven cache misses rather than one.
 */
export interface BoardWindow {
  from: Date;
  to: Date;
  /** Distinguishes one window from another in the query cache. */
  key: string;
}

/**
 * The jobs in a window, plus everything still unscheduled.
 *
 * Keyed by the window's own key rather than by the computed instants, so the
 * same day or week always produces the same key and paging back to yesterday
 * is a cache hit instead of a refetch — which is most of what "feels instant"
 * means once the first load is done.
 *
 * `includeUnscheduled` is what fills the side rail. Jobs with no schedule at
 * all belong to no day, so they come back on every request rather than being
 * invisible until someone thinks to look for them.
 */
/**
 * The server's own maximum (`JobEndpoints.MaxPageSize`). Asking for more is a
 * 400, not a larger page — which is exactly how this broke.
 */
const BOARD_PAGE_SIZE = 100;

/** 2,000 jobs in one window. Far past a real day or week; a stop, not a target. */
const MAX_BOARD_PAGES = 20;

type JobSummary = components['schemas']['JobResponse'];

export function useBoardJobs(window: BoardWindow | null) {
  return useQuery({
    queryKey: ['jobs', 'board', window?.key ?? null],
    enabled: window !== null,
    queryFn: async () => {
      // `enabled` guarantees this, but the type does not.
      if (!window) {
        return [];
      }
      // The board needs the WHOLE window, and `GET /jobs` is paginated as of
      // M1-15 with a hard server cap of 100 per page (JobEndpoints.MaxPageSize).
      //
      // ⚠️ It used to ask for `pageSize: 500` in one request, reasoning that no
      // account could plausibly fill a bigger page. The same commit that added
      // that line added the cap it exceeds, so every board load returned 400
      // and the board did not render at all between 2026-08-28 and 2026-09-01.
      //
      // Paging rather than clamping to 100, deliberately. A clamp would fix the
      // 400 and introduce something worse: a dispatcher silently not shown a
      // job double-books the technician who is already on it. Missing work must
      // never be the quiet outcome here.
      const items: JobSummary[] = [];
      let page = 1;

      for (;;) {
        const { data, response } = await apiClient.GET('/api/v1/jobs', {
          params: {
            query: {
              scheduledFrom: window.from.toISOString(),
              scheduledTo: window.to.toISOString(),
              includeUnscheduled: true,
              page,
              pageSize: BOARD_PAGE_SIZE,
            },
          },
        });
        if (!response.ok || !data) {
          throw new Error(`GET /api/v1/jobs failed: ${response.status}`);
        }

        items.push(...data.items);

        // Stop on the authoritative count, not on a short page: a final page
        // that happens to be exactly full would otherwise cost one extra
        // request, and a server that returns fewer than asked for any other
        // reason would silently truncate the board.
        if (items.length >= Number(data.totalCount) || data.items.length === 0) {
          break;
        }

        page += 1;

        // A window this size is not a real day's work; it is a bug somewhere
        // upstream. Stop rather than hammering the API, and say so — a board
        // that quietly shows a subset is the failure this whole approach
        // exists to avoid.
        if (page > MAX_BOARD_PAGES) {
          console.warn(
            `Dispatch board stopped after ${MAX_BOARD_PAGES} pages ` +
              `(${items.length} of ${data.totalCount} jobs). The board is incomplete.`,
          );
          break;
        }
      }

      return items;
    },
  });
}

/**
 * Availability, licences and skills for every technician, across the days the
 * board is showing (M1-39).
 *
 * <b>ONE request for the whole board</b>, not one per technician and not one
 * per day. M1-37 established the first half of that rule for the roster badge;
 * the second half is why the API takes a WINDOW rather than a date, since the
 * week view greys individual technician-by-day cells and would otherwise need
 * seven calls to draw one week.
 *
 * `users.read`-gated, exactly like the `GET /api/v1/users` the board already
 * makes for its columns, so there is no partial-permission case: anyone who can
 * see the columns can see what is in them.
 *
 * ⚠️ <b>A failure THROWS, and the board degrades on `data` being undefined.</b>
 * The degradation is the same one M1-37's roster badge chose — nothing greyed,
 * no warnings, the jobs still on screen, because a board that greyed people out
 * because a request failed would be telling a dispatcher something false about
 * their crew. What is deliberately NOT the same is swallowing the failure into
 * a successful empty answer, which an earlier draft did:
 *
 * - React Query cached that empty answer as fresh for `staleTime`, so a
 *   transient 500 meant five minutes of confident emptiness with no retry —
 *   `queryFn` never threw, so there was nothing to retry.
 * - `undefined` and "the server says this person has nothing" became the same
 *   value, and the tooltip rendered the second for the first: a technician with
 *   an EXPIRED licence was shown as "No licences recorded", which is a positive
 *   claim nobody made and the opposite of the safe direction.
 *
 * Throwing keeps the degradation and gives the caller `isPending`/`isError` to
 * tell the two apart with. `retry: 1` rather than the default three: a 403 is
 * not going to become a 200, and the board is not worth four round trips.
 */
export function useTechnicianDays(window: { from: CivilDate; to: CivilDate } | null) {
  const fromIso = window ? toIsoDate(window.from) : null;
  const toIso = window ? toIsoDate(window.to) : null;

  return useQuery({
    queryKey: ['dispatch', 'technician-days', fromIso, toIso],
    enabled: fromIso !== null && toIso !== null,
    queryFn: async (): Promise<TechnicianBoardData> => {
      if (!fromIso || !toIso) {
        return indexTechnicianDays([]);
      }
      const { data, response } = await apiClient.GET('/api/v1/dispatch/technician-days', {
        params: { query: { from: fromIso, to: toIso } },
      });
      // `!response.ok || !data` rather than `if (error)`: openapi-fetch only
      // populates `error` when the failure body parses as the declared
      // problem+json, and a gateway 502 is an HTML page (useAccount records
      // this at length).
      if (!response.ok || !data) {
        throw new Error(`GET technician-days failed: ${response.status}`);
      }
      return indexTechnicianDays(data);
    },
    retry: 1,
    staleTime: 5 * 60_000,
  });
}

/**
 * Whether the board has its people-detail answer yet (M1-39).
 *
 * Threaded to the tooltip so it can say "not loaded" instead of "none
 * recorded" — two sentences a dispatcher would act on differently, and which
 * an `undefined` summary alone cannot tell apart.
 */
export type TechnicianDetailStatus = 'pending' | 'ready' | 'unavailable';

/**
 * The board's day boundaries, or null until the account's timezone is known.
 *
 * Deliberately not defaulted to the browser's zone while loading: the two
 * usually agree, so a placeholder would render a board that is right often
 * enough for the wrong one to look like a glitch rather than a bug.
 */
export function useBoardDay(date: CivilDate | null, timeZone: string | undefined): BoardDay | null {
  // Memoised on the DATE, not the object: `date` is re-parsed from the URL on
  // every render, and a new BoardDay each time made every memo downstream of
  // it — the board's cards, the rail, the conflicts, the drop handler —
  // recompute on every render, a drag's every pointermove included (found in
  // review, M2-52, when the card expansion joined them).
  // Built from the string, so the memo reads nothing its key does not name.
  const isoDate = date ? toIsoDate(date) : null;
  return useMemo(() => {
    const civil = isoDate === null ? null : parseIsoDate(isoDate);
    return civil && timeZone ? boardDay(civil, timeZone) : null;
  }, [isoDate, timeZone]);
}

/** The same, for the week containing `date`. */
export function useBoardWeek(
  date: CivilDate | null,
  timeZone: string | undefined,
): BoardWeek | null {
  // Memoised on the date's value, for the reason `useBoardDay` gives.
  // Built from the string, so the memo reads nothing its key does not name.
  const isoDate = date ? toIsoDate(date) : null;
  return useMemo(() => {
    const civil = isoDate === null ? null : parseIsoDate(isoDate);
    return civil && timeZone ? boardWeek(civil, timeZone) : null;
  }, [isoDate, timeZone]);
}
